/**
 * How long a newly-registered texting number takes to go live with the carriers.
 *
 * Attaching a number to the campaign is instant on our side, but the carriers register it
 * out-of-band. Twilio documents this as "several days", and says to allow up to two weeks
 * before treating it as a problem. Until it completes, sends from that number come back
 * undelivered as "unregistered number" — which looks like a broken account to the agent.
 *
 * So whenever a number was switched recently, every send surface warns first.
 */
export const ACTIVATION_WINDOW_DAYS = 14;

export interface ActivationState {
  /** Still inside the carrier registration window. */
  activating: boolean;
  /** Whole days since the switch. */
  daysSinceSwitch: number;
  /** Short line for a banner. */
  message: string;
}

/**
 * @param linkedAt when the number became the registered texting number (ISO string or Date)
 */
export function getTextingNumberActivation(
  linkedAt?: string | Date | null,
): ActivationState | null {
  if (!linkedAt) return null;
  const t = linkedAt instanceof Date ? linkedAt.getTime() : Date.parse(linkedAt);
  if (!Number.isFinite(t)) return null;

  const days = Math.floor((Date.now() - t) / 86_400_000);
  if (days < 0 || days >= ACTIVATION_WINDOW_DAYS) return null;

  return {
    activating: true,
    daysSinceSwitch: days,
    message:
      days === 0
        ? 'Your texting number was just changed. It can take several days for the phone carriers to activate it — texts sent before then may not be delivered.'
        : `Your texting number was changed ${days} day${days === 1 ? '' : 's'} ago. It can take several days for the phone carriers to activate it — texts sent before then may not be delivered.`,
  };
}
